import { Download, Plus, Upload } from 'lucide-react';
import { LiksBusyLabel } from '../../../components/liks/LiksBusyLabel/LiksBusyLabel';
import { LiksButton } from '../../../components/liks/LiksButton/LiksButton';
import { LiksRefreshButton } from '../../../components/liks/LiksRefreshButton/LiksRefreshButton';
import { LiksSearch } from '../../../components/liks/LiksSearch/LiksSearch';
import type { TrainingCorrectionsProgress } from '../../../features/chatbots/trainingCorrectionsProgress';
import { formatTrainingCorrectionsProgressLabel } from '../../../features/chatbots/trainingCorrectionsProgress';
import { useI18n } from '../../../i18n/useI18n';

type ChatbotTrainingToolbarProps = {
  searchInput: string;
  onSearchChange: (value: string) => void;
  canManageTraining: boolean;
  isImportExportBusy: boolean;
  importProgress: TrainingCorrectionsProgress | null;
  exportProgress: TrainingCorrectionsProgress | null;
  isImportPending: boolean;
  isExportPending: boolean;
  onCreateClick: () => void;
  onImportClick: () => void;
  onExport: () => void;
  onManualRefresh: () => void;
};

export function ChatbotTrainingToolbar({
  searchInput,
  onSearchChange,
  canManageTraining,
  isImportExportBusy,
  importProgress,
  exportProgress,
  isImportPending,
  isExportPending,
  onCreateClick,
  onImportClick,
  onExport,
  onManualRefresh,
}: ChatbotTrainingToolbarProps) {
  const { translate } = useI18n();

  return (
    <div className="chatbot-training__toolbar">
      <LiksSearch
        className="chatbot-training__search"
        variant="on-light"
        value={searchInput}
        onChange={onSearchChange}
        placeholder={translate('chatbots.training.searchPlaceholder')}
        aria-label={translate('chatbots.training.searchLabel')}
        clearLabel={translate('common.search.clear')}
      />
      {canManageTraining && (
        <>
          <LiksButton
            type="button"
            className="chatbot-training__create-button"
            disabled={isImportExportBusy}
            onClick={onCreateClick}
          >
            <Plus strokeWidth={2} aria-hidden />
            {translate('chatbots.training.create')}
          </LiksButton>
          <LiksButton
            type="button"
            className={[
              'chatbot-training__import-button',
              isImportPending && 'liks-button--busy',
            ]
              .filter(Boolean)
              .join(' ')}
            variant="secondary"
            disabled={isImportExportBusy}
            onClick={onImportClick}
          >
            {isImportPending ? (
              <LiksBusyLabel>
                {formatTrainingCorrectionsProgressLabel(
                  translate('chatbots.training.importing'),
                  importProgress,
                  true,
                )}
              </LiksBusyLabel>
            ) : (
              <>
                <Upload aria-hidden />
                {translate('chatbots.training.import')}
              </>
            )}
          </LiksButton>
          <LiksButton
            type="button"
            className={[
              'chatbot-training__export-button',
              isExportPending && 'liks-button--busy',
            ]
              .filter(Boolean)
              .join(' ')}
            variant="secondary"
            disabled={isImportExportBusy}
            onClick={onExport}
          >
            {isExportPending ? (
              <LiksBusyLabel>
                {formatTrainingCorrectionsProgressLabel(
                  translate('chatbots.training.exporting'),
                  exportProgress,
                  true,
                )}
              </LiksBusyLabel>
            ) : (
              <>
                <Download aria-hidden />
                {translate('chatbots.training.export')}
              </>
            )}
          </LiksButton>
        </>
      )}
      <LiksRefreshButton
        className="chatbot-training__refresh-button"
        disabled={isImportExportBusy}
        onRefresh={onManualRefresh}
      />
    </div>
  );
}
